import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import NotFoundPage from '../pages/NotFoundPage'
import { LinearProgress } from '@mui/material'
import {
  customerRoutesConfig,
  managerRoutesConfig,
  ownerRoutesConfig,
  publicRoutesConfig,
  adminRoutesConfig,
  waiterRoutesConfig,
  useOwnerMenuItems,
  useAdminMenuItems,
  useManagerMenuItems,
  useWaiterMenuItems,
} from '../config/routes.config'
import { useAuth } from '../hooks/useAuth'
import PublicRoute from './PublicRoute'
import UnauthorizedPage from '../pages/UnauthorizedPage'
import { UserRoles } from '../types/userRoles'
import { generateCustomerRoutes, generateProtectedRoutes } from '../utils/routeUtils'
import { RootRedirect } from './RootRedirect'
import { usePricingPlan } from '../features/Configuration/GeneralConfiguration/hooks/usePricingPlan'
import { PlanLevel } from '../features/Configuration/GeneralConfiguration/types/types'
import { userAtom } from '../stores/userAtom'

const STAFF_ROLES_REQUIRING_PRICING_PLAN: UserRoles[] = [UserRoles.OWNER, UserRoles.MANAGER, UserRoles.WAITER]

const AppRoutes: React.FC = () => {
  const { loading } = useAuth()
  const [user] = useAtom(userAtom)
  const needsPricingPlan = user?.role !== undefined && STAFF_ROLES_REQUIRING_PRICING_PLAN.includes(user.role)
  const { data: pricingPlan = PlanLevel.Starter, isLoading: pricingPlanLoading } = usePricingPlan({ enabled: needsPricingPlan })
  const ownerMenuItems = useOwnerMenuItems()
  const adminMenuItems = useAdminMenuItems()
  const managerMenuItems = useManagerMenuItems()
  const waiterMenuItems = useWaiterMenuItems()

  if (loading || (needsPricingPlan && pricingPlanLoading)) {
    return <LinearProgress sx={{ height: '5px' }} />
  }

  return (
    <Routes>
      {/*----------- Root Path Redirect -----------*/}
      <Route path="/" element={<RootRedirect />} />

      {/*----------- Public Routes -----------*/}
      {publicRoutesConfig.map((route) => (
        <Route key={route.path} path={route.path} element={<PublicRoute>{route.element}</PublicRoute>} />
      ))}
      {/*----------- Customer Routes -----------*/}
      {generateCustomerRoutes(customerRoutesConfig)}

      {/*----------- Manager Routes -----------*/}
      {pricingPlan >= PlanLevel.Standard && generateProtectedRoutes(managerRoutesConfig, [UserRoles.MANAGER], managerMenuItems)}
      {/*----------- Waiter Routes -----------*/}
      {pricingPlan >= PlanLevel.Standard && generateProtectedRoutes(waiterRoutesConfig, [UserRoles.WAITER], waiterMenuItems)}
      {/*----------- Owner Routes -----------*/}
      {generateProtectedRoutes(ownerRoutesConfig, [UserRoles.OWNER], ownerMenuItems, pricingPlan)}

      {/*----------- Admin Routes -----------*/}
      {generateProtectedRoutes(adminRoutesConfig, [UserRoles.ADMIN], adminMenuItems)}

      <Route path="*" element={<Navigate to="/404" />} />
      {/*----------- 404 Not Found -----------*/}
      <Route path="/404" element={<NotFoundPage />} />
      {/*----------- 403 Unauthorized -----------*/}
      <Route path="/403" element={<UnauthorizedPage />} />
    </Routes>
  )
}

export default AppRoutes
